<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Integration from './Integration.vue';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const store = useStore();
const { t } = useI18n();

const accountId = computed(() => store.getters.getCurrentAccountId);

const integrationLoaded = ref(false);
const dialogRef = ref(null);
const whatsappInboxId = ref('');
const templateMappingRaw = ref('');
const conditionalTemplateRulesRaw = ref('');
const errorMessage = ref('');

const integration = computed(() => {
  const record = store.getters['integrations/getIntegration']('external_notifications');
  return record?.id ? record : null;
});

const isConnected = computed(() => (integration.value?.hooks?.length ?? 0) > 0);

const currentHook = computed(() => integration.value?.hooks?.[0] ?? null);

const webhookToken = computed(() => currentHook.value?.access_token ?? '');

const webhookUrl = computed(() => {
  if (!accountId.value) return '';
  return `${window.location.origin}/webhooks/external_notifications/${accountId.value}`;
});

const uiFlags = computed(() => store.getters['integrations/getUIFlags']);

const openConnectDialog = () => {
  errorMessage.value = '';
  dialogRef.value?.open();
};

const cancelConnect = () => {
  whatsappInboxId.value = '';
  templateMappingRaw.value = '';
  conditionalTemplateRulesRaw.value = '';
  errorMessage.value = '';
  dialogRef.value?.close();
};

const parseOptionalJsonArray = raw => {
  if (!raw?.trim()) return [];
  return JSON.parse(raw);
};

const buildSettings = () => {
  if (!whatsappInboxId.value || !templateMappingRaw.value) {
    errorMessage.value = t('INTEGRATION_SETTINGS.EXTERNAL_NOTIFICATIONS.ERRORS.REQUIRED_FIELDS');
    return null;
  }
  let templateMapping;
  try {
    templateMapping = JSON.parse(templateMappingRaw.value);
  } catch {
    errorMessage.value = t('INTEGRATION_SETTINGS.EXTERNAL_NOTIFICATIONS.ERRORS.INVALID_JSON');
    return null;
  }
  if (!templateMapping || typeof templateMapping !== 'object' || Array.isArray(templateMapping)) {
    errorMessage.value = t('INTEGRATION_SETTINGS.EXTERNAL_NOTIFICATIONS.ERRORS.INVALID_JSON');
    return null;
  }

  let conditionalTemplateRules = [];
  try {
    conditionalTemplateRules = parseOptionalJsonArray(conditionalTemplateRulesRaw.value);
  } catch {
    errorMessage.value = t(
      'INTEGRATION_SETTINGS.EXTERNAL_NOTIFICATIONS.ERRORS.INVALID_CONDITIONAL_RULES'
    );
    return null;
  }
  if (!Array.isArray(conditionalTemplateRules)) {
    errorMessage.value = t(
      'INTEGRATION_SETTINGS.EXTERNAL_NOTIFICATIONS.ERRORS.INVALID_CONDITIONAL_RULES'
    );
    return null;
  }

  return {
    whatsapp_inbox_id: whatsappInboxId.value,
    template_mapping: templateMapping,
    conditional_template_rules: conditionalTemplateRules,
  };
};

const activate = async () => {
  const settings = buildSettings();
  if (!settings) return;

  try {
    await store.dispatch('integrations/connectExternalNotifications', settings);
    loadSettingsFromHook();
    dialogRef.value?.close();
    useAlert(t('INTEGRATION_SETTINGS.EXTERNAL_NOTIFICATIONS.CONNECTED_MESSAGE'));
  } catch {
    errorMessage.value = t('INTEGRATION_SETTINGS.EXTERNAL_NOTIFICATIONS.ERRORS.CONNECTION_FAILED');
  }
};

const saveSettings = async () => {
  const settings = buildSettings();
  if (!settings) return;

  try {
    await store.dispatch('integrations/updateExternalNotificationsSettings', {
      hookId: currentHook.value.id,
      settings,
    });
    useAlert(t('INTEGRATION_SETTINGS.EXTERNAL_NOTIFICATIONS.SETTINGS_FORM.SAVE_SUCCESS'));
  } catch {
    useAlert(t('INTEGRATION_SETTINGS.EXTERNAL_NOTIFICATIONS.SETTINGS_FORM.SAVE_ERROR'));
  }
};

const disconnect = async () => {
  if (!currentHook.value) return;
  try {
    await store.dispatch('integrations/disconnectExternalNotifications', currentHook.value.id);
    whatsappInboxId.value = '';
    templateMappingRaw.value = '';
    conditionalTemplateRulesRaw.value = '';
    useAlert(t('INTEGRATION_SETTINGS.DELETE.API.SUCCESS_MESSAGE'));
  } catch {
    useAlert(t('INTEGRATION_SETTINGS.WEBHOOK.DELETE.API.ERROR_MESSAGE'));
  }
};

const copyToken = async () => {
  await navigator.clipboard.writeText(webhookToken.value);
  useAlert(t('INTEGRATION_SETTINGS.EXTERNAL_NOTIFICATIONS.WEBHOOK_TOKEN.COPIED'));
};

const copyUrl = async () => {
  await navigator.clipboard.writeText(webhookUrl.value);
};

const conditionalRulesExample = `[
  {
    "event_type": "checkin.reminder",
    "contact_name_contains": ["meson", "mesón"],
    "template_name": "confirmacion_reserva",
    "template_payload_key": "confirmacion",
    "suffix": "meson-confirmacion",
    "hours_before_check_in": 24
  }
]`;

const payloadExample = `{
  "event_id": "reserva-abc123-checkin",
  "event_type": "checkin.reminder",
  "check_in_at": "2026-06-04T19:46:00+02:00",
  "contact": {
    "phone": "+34900000000",
    "name": "Ana Ejemplo Meson"
  },
  "templates": {
    "checkin": {
      "buttons": [{ "type": "url", "parameter": "token-checkin" }]
    },
    "otra_plantilla": {
      "buttons": [{ "type": "url", "parameter": "token-segundo-enlace" }]
    },
    "confirmacion": {
      "variables": {
        "1": "Ana Ejemplo",
        "2": "https://checkin.example.com/token",
        "3": "04/06/2026 19:46"
      }
    }
  }
}`;

const loadSettingsFromHook = () => {
  const s = currentHook.value?.settings ?? {};
  whatsappInboxId.value = s.whatsapp_inbox_id ?? '';
  templateMappingRaw.value = s.template_mapping
    ? JSON.stringify(s.template_mapping, null, 2)
    : '';
  conditionalTemplateRulesRaw.value = s.conditional_template_rules?.length
    ? JSON.stringify(s.conditional_template_rules, null, 2)
    : '';
};

onMounted(async () => {
  try {
    await store.dispatch('integrations/get');
    if (isConnected.value) loadSettingsFromHook();
  } finally {
    integrationLoaded.value = true;
  }
});
</script>

<template>
  <SettingsLayout :is-loading="!integrationLoaded">
    <template #header>
      <BaseSettingsHeader
        :title="$t('INTEGRATION_SETTINGS.EXTERNAL_NOTIFICATIONS.HEADER')"
        description=""
        :back-button-label="$t('INTEGRATION_SETTINGS.HEADER')"
      />
    </template>
    <template #body>
      <div
        v-if="!integration"
        class="p-6 rounded-xl outline outline-1 outline-n-container bg-n-alpha-3 text-n-slate-11 text-sm"
      >
        {{ $t('INTEGRATION_SETTINGS.EXTERNAL_NOTIFICATIONS.ERRORS.INTEGRATION_NOT_FOUND') }}
      </div>
      <div v-else class="flex flex-col gap-6">
        <Integration
          :integration-id="integration.id"
          :integration-logo="integration.logo"
          :integration-name="integration.name"
          :integration-description="integration.description"
          :integration-enabled="isConnected"
          :integration-action="isConnected ? 'disconnect' : ''"
          :action-button-text="$t('INTEGRATION_SETTINGS.EXTERNAL_NOTIFICATIONS.DELETE')"
          :delete-confirmation-text="{
            title: $t('INTEGRATION_SETTINGS.EXTERNAL_NOTIFICATIONS.DELETE_CONFIRMATION.TITLE'),
            message: $t('INTEGRATION_SETTINGS.EXTERNAL_NOTIFICATIONS.DELETE_CONFIRMATION.MESSAGE'),
          }"
          @delete="disconnect"
        >
          <template #action>
            <Button
              teal
              :label="$t('INTEGRATION_SETTINGS.CONNECT.BUTTON_TEXT')"
              @click="openConnectDialog"
            />
          </template>
        </Integration>

        <div
          v-if="isConnected"
          class="flex flex-col gap-4 outline outline-n-container outline-1 bg-n-alpha-3 rounded-md shadow p-6"
        >
          <p class="text-n-teal-9">
            {{ $t('INTEGRATION_SETTINGS.EXTERNAL_NOTIFICATIONS.CONNECTED_MESSAGE') }}
          </p>

          <!-- Webhook URL + Token -->
          <div class="border-t border-n-container pt-4 mt-2 flex flex-col gap-4">
            <div>
              <p class="text-sm font-medium text-n-slate-12 mb-1">
                {{ $t('INTEGRATION_SETTINGS.EXTERNAL_NOTIFICATIONS.WEBHOOK_URL.LABEL') }}
              </p>
              <p class="text-xs text-n-slate-11 mb-2">
                {{ $t('INTEGRATION_SETTINGS.EXTERNAL_NOTIFICATIONS.WEBHOOK_URL.HELP') }}
              </p>
              <div class="flex items-center gap-2">
                <code class="flex-1 bg-n-alpha-2 rounded px-3 py-2 text-xs text-n-slate-12 break-all select-all">
                  {{ webhookUrl }}
                </code>
                <Button slate faded size="small" icon="copy" @click="copyUrl" />
              </div>
            </div>

            <div>
              <p class="text-sm font-medium text-n-slate-12 mb-1">
                {{ $t('INTEGRATION_SETTINGS.EXTERNAL_NOTIFICATIONS.WEBHOOK_TOKEN.LABEL') }}
              </p>
              <p class="text-xs text-n-slate-11 mb-2">
                {{ $t('INTEGRATION_SETTINGS.EXTERNAL_NOTIFICATIONS.WEBHOOK_TOKEN.HELP') }}
              </p>
              <div class="flex items-center gap-2">
                <code class="flex-1 bg-n-alpha-2 rounded px-3 py-2 text-xs text-n-slate-12 break-all select-all font-mono">
                  {{ webhookToken }}
                </code>
                <Button slate faded size="small" icon="copy" @click="copyToken" />
              </div>
            </div>

            <div>
              <p class="text-sm font-medium text-n-slate-12 mb-1">
                {{ $t('INTEGRATION_SETTINGS.EXTERNAL_NOTIFICATIONS.PAYLOAD_EXAMPLE.LABEL') }}
              </p>
              <p class="text-xs text-n-slate-11 mb-2">
                {{ $t('INTEGRATION_SETTINGS.EXTERNAL_NOTIFICATIONS.PAYLOAD_EXAMPLE.HELP') }}
              </p>
              <pre class="bg-n-alpha-2 rounded px-3 py-2 text-xs text-n-slate-12 overflow-x-auto whitespace-pre-wrap select-all">{{ payloadExample }}</pre>
              <p class="text-xs text-n-slate-11 mt-3 mb-2">
                {{ $t('INTEGRATION_SETTINGS.EXTERNAL_NOTIFICATIONS.CONDITIONAL_RULES_EXAMPLE.LABEL') }}
              </p>
              <pre class="bg-n-alpha-2 rounded px-3 py-2 text-xs text-n-slate-12 overflow-x-auto whitespace-pre-wrap select-all">{{ conditionalRulesExample }}</pre>
            </div>
          </div>

          <!-- Settings form -->
          <div class="border-t border-n-container pt-4 mt-2">
            <h3 class="text-sm font-medium text-n-slate-12 mb-1">
              {{ $t('INTEGRATION_SETTINGS.EXTERNAL_NOTIFICATIONS.SETTINGS_FORM.TITLE') }}
            </h3>
            <p class="text-xs text-n-slate-11 mb-4">
              {{ $t('INTEGRATION_SETTINGS.EXTERNAL_NOTIFICATIONS.SETTINGS_FORM.DESCRIPTION') }}
            </p>
            <div class="flex flex-col gap-4">
              <Input
                v-model="whatsappInboxId"
                :label="$t('INTEGRATION_SETTINGS.EXTERNAL_NOTIFICATIONS.CONNECT_FORM.WHATSAPP_INBOX_ID')"
                :placeholder="$t('INTEGRATION_SETTINGS.EXTERNAL_NOTIFICATIONS.CONNECT_FORM.WHATSAPP_INBOX_ID_PLACEHOLDER')"
                :help-text="$t('INTEGRATION_SETTINGS.EXTERNAL_NOTIFICATIONS.CONNECT_FORM.WHATSAPP_INBOX_ID_HELP')"
              />
              <Input
                v-model="templateMappingRaw"
                :label="$t('INTEGRATION_SETTINGS.EXTERNAL_NOTIFICATIONS.CONNECT_FORM.TEMPLATE_MAPPING')"
                :placeholder="$t('INTEGRATION_SETTINGS.EXTERNAL_NOTIFICATIONS.CONNECT_FORM.TEMPLATE_MAPPING_PLACEHOLDER')"
                :help-text="$t('INTEGRATION_SETTINGS.EXTERNAL_NOTIFICATIONS.CONNECT_FORM.TEMPLATE_MAPPING_HELP')"
              />
              <Input
                v-model="conditionalTemplateRulesRaw"
                :label="$t('INTEGRATION_SETTINGS.EXTERNAL_NOTIFICATIONS.CONNECT_FORM.CONDITIONAL_RULES')"
                :placeholder="$t('INTEGRATION_SETTINGS.EXTERNAL_NOTIFICATIONS.CONNECT_FORM.CONDITIONAL_RULES_PLACEHOLDER')"
                :help-text="$t('INTEGRATION_SETTINGS.EXTERNAL_NOTIFICATIONS.CONNECT_FORM.CONDITIONAL_RULES_HELP')"
              />
              <p v-if="errorMessage" class="text-n-ruby-9 text-sm">
                {{ errorMessage }}
              </p>
              <div class="flex justify-end">
                <Button
                  teal
                  :label="$t('INTEGRATION_SETTINGS.EXTERNAL_NOTIFICATIONS.SETTINGS_FORM.SAVE')"
                  :is-loading="uiFlags.isUpdatingExternalNotifications"
                  @click="saveSettings"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Connect dialog -->
        <Dialog
          ref="dialogRef"
          :title="$t('INTEGRATION_SETTINGS.EXTERNAL_NOTIFICATIONS.CONNECT_FORM.TITLE')"
          :description="$t('INTEGRATION_SETTINGS.EXTERNAL_NOTIFICATIONS.CONNECT_FORM.DESCRIPTION')"
          :confirm-button-label="$t('INTEGRATION_SETTINGS.EXTERNAL_NOTIFICATIONS.CONNECT_FORM.SUBMIT')"
          :cancel-button-label="$t('INTEGRATION_SETTINGS.EXTERNAL_NOTIFICATIONS.CONNECT_FORM.CANCEL')"
          :is-loading="uiFlags.isCreatingExternalNotifications"
          :disable-confirm-button="!whatsappInboxId || !templateMappingRaw"
          @confirm="activate"
          @close="cancelConnect"
        >
          <div class="flex flex-col gap-4 mt-2">
            <Input
              v-model="whatsappInboxId"
              :label="$t('INTEGRATION_SETTINGS.EXTERNAL_NOTIFICATIONS.CONNECT_FORM.WHATSAPP_INBOX_ID')"
              :placeholder="$t('INTEGRATION_SETTINGS.EXTERNAL_NOTIFICATIONS.CONNECT_FORM.WHATSAPP_INBOX_ID_PLACEHOLDER')"
              :help-text="$t('INTEGRATION_SETTINGS.EXTERNAL_NOTIFICATIONS.CONNECT_FORM.WHATSAPP_INBOX_ID_HELP')"
            />
            <Input
              v-model="templateMappingRaw"
              :label="$t('INTEGRATION_SETTINGS.EXTERNAL_NOTIFICATIONS.CONNECT_FORM.TEMPLATE_MAPPING')"
              :placeholder="$t('INTEGRATION_SETTINGS.EXTERNAL_NOTIFICATIONS.CONNECT_FORM.TEMPLATE_MAPPING_PLACEHOLDER')"
              :help-text="$t('INTEGRATION_SETTINGS.EXTERNAL_NOTIFICATIONS.CONNECT_FORM.TEMPLATE_MAPPING_HELP')"
            />
            <Input
              v-model="conditionalTemplateRulesRaw"
              :label="$t('INTEGRATION_SETTINGS.EXTERNAL_NOTIFICATIONS.CONNECT_FORM.CONDITIONAL_RULES')"
              :placeholder="$t('INTEGRATION_SETTINGS.EXTERNAL_NOTIFICATIONS.CONNECT_FORM.CONDITIONAL_RULES_PLACEHOLDER')"
              :help-text="$t('INTEGRATION_SETTINGS.EXTERNAL_NOTIFICATIONS.CONNECT_FORM.CONDITIONAL_RULES_HELP')"
            />
            <p v-if="errorMessage" class="text-n-ruby-9 text-sm">
              {{ errorMessage }}
            </p>
          </div>
        </Dialog>
      </div>
    </template>
  </SettingsLayout>
</template>
